import { formatDistanceToNow } from 'date-fns';
import {
  Activity,
  CheckCircle2,
  Clock,
  DollarSign,
  MessagesSquare,
  Wrench,
  XCircle,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { useChatOverview, useChatRuns } from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(225, 20%, 16%)',
  border: '1px solid hsl(225, 15%, 25%)',
  borderRadius: '8px',
  color: 'hsl(0, 0%, 95%)',
};

const GRID_STROKE = 'hsl(225, 15%, 18%)';
const AXIS_STROKE = 'hsl(0, 0%, 45%)';
const PRIMARY_COLOR = 'hsl(184, 92%, 56%)';
const SUCCESS_COLOR = 'hsl(142, 71%, 45%)';
const DANGER_COLOR = 'hsl(0, 84%, 60%)';

const STATUS_VARIANT: Record<string, 'success' | 'danger' | 'default'> = {
  completed: 'success',
  failed: 'danger',
};

function formatDuration(ms: number | null) {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function ChartSkeleton() {
  return (
    <div className="h-64 flex items-center justify-center">
      <div className="text-text-muted">Loading...</div>
    </div>
  );
}

export function ChatDashboard() {
  const { data: overview, isLoading: overviewLoading } = useChatOverview();
  const { data: runs, isLoading: runsLoading } = useChatRuns({ page: 1, limit: 20 });

  const statusPieData = overview
    ? [
        { name: 'Completed', value: overview.successCount, color: SUCCESS_COLOR },
        { name: 'Failed', value: overview.failCount, color: DANGER_COLOR },
      ].filter((s) => s.value > 0)
    : [];

  const toolsChartData =
    overview?.toolsBreakdown.map((t) => ({ name: t.toolName, count: t.count })) ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Chat Orchestrator</h1>
        <p className="text-sm text-text-muted mt-1">
          Overview of orchestrator chat runs (agentType=orchestrator)
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Runs"
          value={overview?.runCount ?? 0}
          icon={MessagesSquare}
          loading={overviewLoading}
        />
        <StatCard
          label="Tool Calls"
          value={overview?.childCount ?? 0}
          icon={Wrench}
          loading={overviewLoading}
        />
        <StatCard
          label="Avg Duration"
          value={formatDuration(overview?.avgDurationMs ?? null)}
          icon={Clock}
          loading={overviewLoading}
        />
        <StatCard
          label="Total Tokens"
          value={overview?.totalTokens ?? 0}
          icon={Activity}
          loading={overviewLoading}
        />
        <StatCard
          label="LLM Cost"
          value={overview ? `$${overview.costUsd}` : '$0.00'}
          icon={DollarSign}
          loading={overviewLoading}
        />
      </div>

      {overview?.costNote && (
        <p className="text-xs text-text-muted italic px-1">{overview.costNote}</p>
      )}

      {/* Status breakdown mini cards (plain counts, independent of chart rendering) */}
      <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 max-w-md">
        <div className="flex items-center gap-3 bg-surface rounded-lg border border-border p-3">
          <CheckCircle2 size={18} className="text-success" />
          <div>
            <div className="text-lg font-semibold text-text">
              {overviewLoading ? '-' : (overview?.successCount ?? 0)}
            </div>
            <div className="text-xs text-text-muted">Completed</div>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-surface rounded-lg border border-border p-3">
          <XCircle size={18} className="text-danger" />
          <div>
            <div className="text-lg font-semibold text-text">
              {overviewLoading ? '-' : (overview?.failCount ?? 0)}
            </div>
            <div className="text-xs text-text-muted">Failed</div>
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Run Status</h2>
          {overviewLoading ? (
            <ChartSkeleton />
          ) : statusPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  dataKey="value"
                  nameKey="name"
                  paddingAngle={2}
                  label={({ name, value }) => `${name} (${value})`}
                >
                  {statusPieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>

        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Tools Breakdown</h2>
          {overviewLoading ? (
            <ChartSkeleton />
          ) : toolsChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={toolsChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis type="number" stroke={AXIS_STROKE} fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke={AXIS_STROKE}
                  fontSize={11}
                  width={160}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Calls" radius={[0, 4, 4, 0]} fill={PRIMARY_COLOR} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
          {toolsChartData.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-text-secondary">
              {overview?.toolsBreakdown.map((tool) => (
                <li key={tool.toolName} className="flex items-center justify-between">
                  <span>{tool.toolName}</span>
                  <span className="text-text-muted">{tool.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent runs table */}
      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <MessagesSquare size={16} className="text-primary" />
          <h2 className="text-sm font-medium text-text-secondary">Recent Runs</h2>
          {runs && <span className="text-xs text-text-muted">({runs.total} total)</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-5 py-3 font-medium">Run</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Duration</th>
                <th className="px-5 py-3 font-medium">Cost</th>
                <th className="px-5 py-3 font-medium">Error</th>
                <th className="px-5 py-3 font-medium">Started</th>
              </tr>
            </thead>
            <tbody>
              {runsLoading ? (
                ['r1', 'r2', 'r3'].map((rk) => (
                  <tr key={rk} className="border-b border-border animate-pulse">
                    {['c1', 'c2', 'c3', 'c4', 'c5', 'c6'].map((ck) => (
                      <td key={ck} className="px-5 py-3">
                        <div className="h-4 bg-border rounded w-16" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : runs?.data?.length ? (
                runs.data.map((run) => {
                  const variant = STATUS_VARIANT[run.status] ?? 'default';
                  const StatusIcon = run.status === 'completed' ? CheckCircle2 : XCircle;
                  return (
                    <tr
                      key={run.id}
                      onClick={() => navigate(`/chat-dashboard/${run.id}`)}
                      className="border-b border-border hover:bg-surface-hover transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-3 text-text font-medium">{run.id}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5">
                          <StatusIcon size={14} />
                          <Badge variant={variant}>{run.status}</Badge>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-text-secondary">
                        {formatDuration(run.durationMs)}
                      </td>
                      <td className="px-5 py-3 text-text-secondary">
                        {parseFloat(run.costUsd) > 0 ? `$${parseFloat(run.costUsd).toFixed(4)}` : '-'}
                      </td>
                      <td className="px-5 py-3 text-danger text-xs truncate max-w-[200px]">
                        {run.errorMessage ?? '-'}
                      </td>
                      <td className="px-5 py-3 text-text-muted text-xs whitespace-nowrap">
                        {run.startedAt
                          ? formatDistanceToNow(new Date(run.startedAt), { addSuffix: true })
                          : '-'}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-text-muted">
                    No chat orchestrator runs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
